import { useState } from 'react';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator, Home, TrendingDown, TrendingUp, DollarSign, Wallet } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { formatCurrency } from '@/utils/currency';

export default function LoanCalculators({ user, onLogout }) {
  // EMI Calculator State
  const [emiData, setEmiData] = useState({
    loanAmount: '',
    interestRate: 10,
    loanTenure: 12,
    tenureType: 'years' // 'years' or 'months'
  });
  const [emiResult, setEmiResult] = useState(null);

  // Prepayment Calculator State
  const [prepaymentData, setPrepaymentData] = useState({
    loanAmount: '',
    interestRate: 10,
    loanTenure: 12,
    emi: '',
    prepaymentAmount: '',
    prepaymentAfter: 12
  });
  const [prepaymentResult, setPrepaymentResult] = useState(null);

  // Loan Eligibility Calculator State
  const [eligibilityData, setEligibilityData] = useState({
    monthlyIncome: '',
    existingEmi: '',
    interestRate: 10,
    loanTenure: 12,
    foirPercentage: 50
  });
  const [eligibilityResult, setEligibilityResult] = useState(null);

  // Calculate EMI
  const calculateEMI = () => {
    const P = parseFloat(emiData.loanAmount);
    const annualRate = parseFloat(emiData.interestRate);
    const r = annualRate / 100 / 12; // Monthly interest rate
    let n = parseFloat(emiData.loanTenure);
    
    // Convert to months if tenure is in years
    if (emiData.tenureType === 'years') {
      n = n * 12;
    }

    if (!P || !n || P <= 0 || n <= 0) return;

    // EMI Formula: EMI = [P x R x (1+R)^N]/[(1+R)^N-1]
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const totalAmount = emi * n;
    const totalInterest = totalAmount - P;

    // Calculate breakdown for pie chart
    const principalPercentage = (P / totalAmount) * 100;
    const interestPercentage = (totalInterest / totalAmount) * 100;

    setEmiResult({
      monthlyEmi: emi,
      totalAmount: totalAmount,
      totalInterest: totalInterest,
      principalAmount: P,
      principalPercentage: principalPercentage,
      interestPercentage: interestPercentage,
      chartData: [
        { name: 'Principal', value: P },
        { name: 'Interest', value: totalInterest }
      ]
    });
  };

  // Calculate Prepayment
  const calculatePrepayment = () => {
    const P = parseFloat(prepaymentData.loanAmount);
    const annualRate = parseFloat(prepaymentData.interestRate);
    const r = annualRate / 100 / 12;
    const n = parseFloat(prepaymentData.loanTenure) * 12;
    const emi = parseFloat(prepaymentData.emi);
    const prepayAmount = parseFloat(prepaymentData.prepaymentAmount);
    const prepayAfter = parseFloat(prepaymentData.prepaymentAfter);

    if (!P || !n || !emi || !prepayAmount || P <= 0 || n <= 0 || emi <= 0 || prepayAmount <= 0) return;

    // Calculate original loan details
    const totalAmountWithoutPrepay = emi * n;
    const totalInterestWithoutPrepay = totalAmountWithoutPrepay - P;

    // Calculate outstanding principal after prepayment months
    let outstandingPrincipal = P;
    for (let month = 1; month <= prepayAfter; month++) {
      const interestPortion = outstandingPrincipal * r;
      const principalPortion = emi - interestPortion;
      outstandingPrincipal -= principalPortion;
    }

    // Reduce principal by prepayment amount
    const newPrincipal = outstandingPrincipal - prepayAmount;

    // Calculate new tenure with same EMI
    let newTenure = 0;
    let balance = newPrincipal;
    while (balance > 0 && newTenure < 1000) {
      const interestPortion = balance * r;
      const principalPortion = emi - interestPortion;
      balance -= principalPortion;
      newTenure++;
    }

    const totalNewTenure = prepayAfter + newTenure;
    const totalAmountWithPrepay = (emi * totalNewTenure) + prepayAmount;
    const totalInterestWithPrepay = totalAmountWithPrepay - P;
    const interestSaved = totalInterestWithoutPrepay - totalInterestWithPrepay;
    const tenureReduced = n - totalNewTenure;

    setPrepaymentResult({
      interestSaved: interestSaved,
      tenureReduced: tenureReduced,
      newTenure: totalNewTenure,
      originalTenure: n,
      originalInterest: totalInterestWithoutPrepay,
      newInterest: totalInterestWithPrepay,
      chartData: [
        { name: 'Without Prepayment', interest: totalInterestWithoutPrepay, tenure: n },
        { name: 'With Prepayment', interest: totalInterestWithPrepay, tenure: totalNewTenure }
      ]
    });
  };

  // Calculate Loan Eligibility
  const calculateEligibility = () => {
    const monthlyIncome = parseFloat(eligibilityData.monthlyIncome);
    const existingEmi = parseFloat(eligibilityData.existingEmi) || 0;
    const annualRate = parseFloat(eligibilityData.interestRate);
    const r = annualRate / 100 / 12;
    const n = parseFloat(eligibilityData.loanTenure) * 12;
    const foirPercentage = parseFloat(eligibilityData.foirPercentage);

    if (!monthlyIncome || !n || monthlyIncome <= 0 || n <= 0) return;

    // Calculate maximum EMI based on FOIR (Fixed Obligation to Income Ratio)
    const maxTotalEmi = (monthlyIncome * foirPercentage) / 100;
    const availableEmi = maxTotalEmi - existingEmi;

    if (availableEmi <= 0) {
      setEligibilityResult({
        maxLoanAmount: 0,
        maxEmi: 0,
        availableEmi: 0,
        foirUsed: (existingEmi / monthlyIncome) * 100,
        message: 'Your existing EMIs exceed the recommended FOIR limit'
      });
      return;
    }

    // Calculate maximum loan amount: P = [EMI x ((1+R)^N-1)] / [R x (1+R)^N]
    const maxLoanAmount = (availableEmi * (Math.pow(1 + r, n) - 1)) / (r * Math.pow(1 + r, n));
    const foirUsed = (maxTotalEmi / monthlyIncome) * 100;

    setEligibilityResult({
      maxLoanAmount: maxLoanAmount,
      maxEmi: availableEmi,
      availableEmi: availableEmi,
      foirUsed: foirUsed,
      existingEmi: existingEmi,
      monthlyIncome: monthlyIncome
    });
  };

  const COLORS = ['#10b981', '#ef4444'];
  const CHART_COLORS = ['#3b82f6', '#10b981'];

  return (
    <div className="min-h-screen p-4 lg:p-8 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto">
        <Navigation user={user} onLogout={onLogout} currentPage="/loan-calculators" />

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center">
              <Calculator className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100">Loan Calculators</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Calculate loan payments and plan your finances</p>
        </div>

        {/* EMI Calculator */}
        <Card className="glass p-6 dark:bg-gray-800 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <Home className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">EMI Calculator</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="loanAmount" className="text-gray-700 dark:text-gray-300">Loan Amount</Label>
                <Input
                  id="loanAmount"
                  type="number"
                  placeholder="Enter loan amount"
                  value={emiData.loanAmount}
                  onChange={(e) => setEmiData({ ...emiData, loanAmount: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="interestRate" className="text-gray-700 dark:text-gray-300">
                  Interest Rate (% p.a.): {emiData.interestRate}%
                </Label>
                <input
                  id="interestRate"
                  type="range"
                  min="1"
                  max="30"
                  step="0.5"
                  value={emiData.interestRate}
                  onChange={(e) => setEmiData({ ...emiData, interestRate: e.target.value })}
                  className="w-full mt-2"
                />
              </div>

              <div>
                <Label htmlFor="loanTenure" className="text-gray-700 dark:text-gray-300">Loan Tenure</Label>
                <div className="flex space-x-2 mt-1">
                  <Input
                    id="loanTenure"
                    type="number"
                    placeholder="Enter tenure"
                    value={emiData.loanTenure}
                    onChange={(e) => setEmiData({ ...emiData, loanTenure: e.target.value })}
                    className="flex-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                  />
                  <select
                    value={emiData.tenureType}
                    onChange={(e) => setEmiData({ ...emiData, tenureType: e.target.value })}
                    className="px-4 py-2 border rounded-md dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                  >
                    <option value="years">Years</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <Button
                onClick={calculateEMI}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white"
              >
                Calculate EMI
              </Button>
            </div>

            {/* Result Section */}
            <div>
              {emiResult ? (
                <div className="space-y-4">
                  <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900 dark:to-indigo-900 border-blue-200 dark:border-blue-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Monthly EMI</p>
                    <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                      {formatCurrency(emiResult.monthlyEmi, user?.currency)}
                    </p>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 dark:bg-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Principal Amount</p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                        {formatCurrency(emiResult.principalAmount, user?.currency)}
                      </p>
                    </Card>

                    <Card className="p-4 dark:bg-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Interest</p>
                      <p className="text-xl font-semibold text-red-600 dark:text-red-400">
                        {formatCurrency(emiResult.totalInterest, user?.currency)}
                      </p>
                    </Card>
                  </div>

                  <Card className="p-4 dark:bg-gray-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Amount Payable</p>
                    <p className="text-2xl font-bold text-gray-800 dark:text-gray-200">
                      {formatCurrency(emiResult.totalAmount, user?.currency)}
                    </p>
                  </Card>

                  {/* Pie Chart */}
                  <Card className="p-4 dark:bg-gray-700">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Payment Breakdown</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={emiResult.chartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                          outerRadius={80}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {emiResult.chartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => formatCurrency(value, user?.currency)} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex justify-center space-x-4 mt-4">
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-emerald-500 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Principal</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <div className="w-4 h-4 bg-red-500 rounded"></div>
                        <span className="text-sm text-gray-700 dark:text-gray-300">Interest</span>
                      </div>
                    </div>
                  </Card>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <TrendingDown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Enter loan details to calculate EMI</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Prepayment Calculator */}
        <Card className="glass p-6 dark:bg-gray-800 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <TrendingUp className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Prepayment Calculator</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="prepay-loanAmount" className="text-gray-700 dark:text-gray-300">Loan Amount</Label>
                <Input
                  id="prepay-loanAmount"
                  type="number"
                  placeholder="Enter loan amount"
                  value={prepaymentData.loanAmount}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, loanAmount: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="prepay-interestRate" className="text-gray-700 dark:text-gray-300">
                  Interest Rate (% p.a.): {prepaymentData.interestRate}%
                </Label>
                <input
                  id="prepay-interestRate"
                  type="range"
                  min="1"
                  max="30"
                  step="0.5"
                  value={prepaymentData.interestRate}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, interestRate: e.target.value })}
                  className="w-full mt-2"
                />
              </div>

              <div>
                <Label htmlFor="prepay-loanTenure" className="text-gray-700 dark:text-gray-300">Loan Tenure (Years)</Label>
                <Input
                  id="prepay-loanTenure"
                  type="number"
                  placeholder="Enter tenure in years"
                  value={prepaymentData.loanTenure}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, loanTenure: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="prepay-emi" className="text-gray-700 dark:text-gray-300">Current Monthly EMI</Label>
                <Input
                  id="prepay-emi"
                  type="number"
                  placeholder="Enter your EMI"
                  value={prepaymentData.emi}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, emi: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="prepay-amount" className="text-gray-700 dark:text-gray-300">Prepayment Amount</Label>
                <Input
                  id="prepay-amount"
                  type="number"
                  placeholder="Enter prepayment amount"
                  value={prepaymentData.prepaymentAmount}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, prepaymentAmount: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="prepay-after" className="text-gray-700 dark:text-gray-300">
                  Prepayment After (Months): {prepaymentData.prepaymentAfter}
                </Label>
                <input
                  id="prepay-after"
                  type="range"
                  min="1"
                  max="240"
                  step="1"
                  value={prepaymentData.prepaymentAfter}
                  onChange={(e) => setPrepaymentData({ ...prepaymentData, prepaymentAfter: e.target.value })}
                  className="w-full mt-2"
                />
              </div>

              <Button
                onClick={calculatePrepayment}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white"
              >
                Calculate Savings
              </Button>
            </div>

            {/* Result Section */}
            <div>
              {prepaymentResult ? (
                <div className="space-y-4">
                  <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900 dark:to-teal-900 border-emerald-200 dark:border-emerald-700">
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Interest Saved</p>
                    <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(prepaymentResult.interestSaved, user?.currency)}
                    </p>
                  </Card>

                  <div className="grid grid-cols-2 gap-4">
                    <Card className="p-4 dark:bg-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Tenure Reduced</p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                        {prepaymentResult.tenureReduced} months
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        ({(prepaymentResult.tenureReduced / 12).toFixed(1)} years)
                      </p>
                    </Card>

                    <Card className="p-4 dark:bg-gray-700">
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">New Tenure</p>
                      <p className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                        {prepaymentResult.newTenure} months
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        ({(prepaymentResult.newTenure / 12).toFixed(1)} years)
                      </p>
                    </Card>
                  </div>

                  {/* Bar Chart */}
                  <Card className="p-4 dark:bg-gray-700">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Comparison</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={prepaymentResult.chartData}>
                        <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <YAxis tick={{ fill: '#9ca3af', fontSize: 12 }} />
                        <Tooltip 
                          formatter={(value, name) => [
                            name === 'interest' ? formatCurrency(value, user?.currency) : `${value} months`,
                            name === 'interest' ? 'Interest' : 'Tenure'
                          ]}
                          contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151' }}
                          labelStyle={{ color: '#f3f4f6' }}
                        />
                        <Bar dataKey="interest" fill="#10b981" name="Interest" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Enter prepayment details to see savings</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Loan Eligibility Calculator */}
        <Card className="glass p-6 dark:bg-gray-800 mb-8">
          <div className="flex items-center space-x-2 mb-6">
            <Wallet className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Loan Eligibility Calculator</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="monthlyIncome" className="text-gray-700 dark:text-gray-300">Monthly Income</Label>
                <Input
                  id="monthlyIncome"
                  type="number"
                  placeholder="Enter monthly income"
                  value={eligibilityData.monthlyIncome}
                  onChange={(e) => setEligibilityData({ ...eligibilityData, monthlyIncome: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="existingEmi" className="text-gray-700 dark:text-gray-300">Existing EMIs (if any)</Label>
                <Input
                  id="existingEmi"
                  type="number"
                  placeholder="Enter existing EMI"
                  value={eligibilityData.existingEmi}
                  onChange={(e) => setEligibilityData({ ...eligibilityData, existingEmi: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="elig-interestRate" className="text-gray-700 dark:text-gray-300">
                  Expected Interest Rate (% p.a.): {eligibilityData.interestRate}%
                </Label>
                <input
                  id="elig-interestRate"
                  type="range"
                  min="1"
                  max="30"
                  step="0.5"
                  value={eligibilityData.interestRate}
                  onChange={(e) => setEligibilityData({ ...eligibilityData, interestRate: e.target.value })}
                  className="w-full mt-2"
                />
              </div>

              <div>
                <Label htmlFor="elig-loanTenure" className="text-gray-700 dark:text-gray-300">Loan Tenure (Years)</Label>
                <Input
                  id="elig-loanTenure"
                  type="number"
                  placeholder="Enter tenure in years"
                  value={eligibilityData.loanTenure}
                  onChange={(e) => setEligibilityData({ ...eligibilityData, loanTenure: e.target.value })}
                  className="mt-1 dark:bg-gray-700 dark:text-gray-100 dark:border-gray-600"
                />
              </div>

              <div>
                <Label htmlFor="foirPercentage" className="text-gray-700 dark:text-gray-300">
                  FOIR (Fixed Obligation to Income Ratio): {eligibilityData.foirPercentage}%
                </Label>
                <input
                  id="foirPercentage"
                  type="range"
                  min="30"
                  max="70"
                  step="5"
                  value={eligibilityData.foirPercentage}
                  onChange={(e) => setEligibilityData({ ...eligibilityData, foirPercentage: e.target.value })}
                  className="w-full mt-2"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Banks typically use 40-50% FOIR
                </p>
              </div>

              <Button
                onClick={calculateEligibility}
                className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white"
              >
                Calculate Eligibility
              </Button>
            </div>

            {/* Result Section */}
            <div>
              {eligibilityResult ? (
                <div className="space-y-4">
                  {eligibilityResult.maxLoanAmount > 0 ? (
                    <>
                      <Card className="p-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900 dark:to-indigo-900 border-purple-200 dark:border-purple-700">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Maximum Loan Amount</p>
                        <p className="text-3xl font-bold text-purple-600 dark:text-purple-400">
                          {formatCurrency(eligibilityResult.maxLoanAmount, user?.currency)}
                        </p>
                      </Card>

                      <div className="grid grid-cols-2 gap-4">
                        <Card className="p-4 dark:bg-gray-700">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Maximum EMI</p>
                          <p className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                            {formatCurrency(eligibilityResult.maxEmi, user?.currency)}
                          </p>
                        </Card>

                        <Card className="p-4 dark:bg-gray-700">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">FOIR Used</p>
                          <p className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                            {eligibilityResult.foirUsed.toFixed(1)}%
                          </p>
                        </Card>
                      </div>

                      <Card className="p-4 dark:bg-gray-700">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">Income Breakdown</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Monthly Income</span>
                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                              {formatCurrency(eligibilityResult.monthlyIncome, user?.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600 dark:text-gray-400">Existing EMIs</span>
                            <span className="font-semibold text-red-600 dark:text-red-400">
                              {formatCurrency(eligibilityResult.existingEmi, user?.currency)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm border-t border-gray-300 dark:border-gray-600 pt-2">
                            <span className="text-gray-600 dark:text-gray-400">Available for New Loan</span>
                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                              {formatCurrency(eligibilityResult.availableEmi, user?.currency)}
                            </span>
                          </div>
                        </div>
                      </Card>
                    </>
                  ) : (
                    <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                      <p className="text-red-800 dark:text-red-300 font-semibold mb-2">Eligibility Issue</p>
                      <p className="text-sm text-red-700 dark:text-red-400">{eligibilityResult.message}</p>
                    </Card>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Calculator className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 dark:text-gray-400">Enter your income details to check eligibility</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Info Card */}
        <Card className="glass p-6 dark:bg-gray-800">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">About Loan Calculators</h3>
          <div className="space-y-3 text-gray-600 dark:text-gray-400 text-sm">
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-300">EMI Calculator</p>
              <p>Calculate your monthly installment for any loan. Formula: EMI = [P × R × (1+R)^N] / [(1+R)^N-1]</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-300">Prepayment Calculator</p>
              <p>See how much interest you can save and how much your loan tenure reduces by making a prepayment on your existing loan.</p>
            </div>
            <div>
              <p className="font-semibold text-gray-700 dark:text-gray-300">Loan Eligibility Calculator</p>
              <p>Calculate the maximum loan amount you're eligible for based on your income and existing EMIs. Uses FOIR (Fixed Obligation to Income Ratio) methodology.</p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
